<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Lock, ArrowLeft, ArrowRight, Loader2, ShieldCheck, Zap, BarChart3, CheckCircle2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const loading = ref(false)
const reset = ref(false)
const token = ref('')
const touched = reactive({ password: false, confirmPassword: false })

const form = reactive({
  password: '',
  confirmPassword: '',
})

const passwordError = computed(() => {
  if (!touched.password) return ''
  if (!form.password) return 'La contraseña es obligatoria'
  return form.password.length < 8 ? 'Mínimo 8 caracteres' : ''
})

const confirmPasswordError = computed(() => {
  if (!touched.confirmPassword) return ''
  if (!form.confirmPassword) return 'Confirma tu contraseña'
  return form.confirmPassword !== form.password ? 'Las contraseñas no coinciden' : ''
})

const isValid = computed(
  () => !passwordError.value && !confirmPasswordError.value && form.password && form.confirmPassword,
)

onMounted(() => {
  const tokenParam = route.query.token as string
  if (!tokenParam) {
    toast.error('Token no válido o expirado')
    router.push('/login')
    return
  }
  token.value = tokenParam
})

const handleSubmit = async () => {
  touched.password = true
  touched.confirmPassword = true
  if (!isValid.value) {
    toast.error('Por favor completa los campos correctamente')
    return
  }
  loading.value = true
  const success = await authStore.resetPassword(token.value, form.password)
  loading.value = false
  if (success) {
    reset.value = true
    toast.success('Contraseña actualizada')
  }
}
</script>

<template>
  <div class="min-h-screen grid md:grid-cols-2 lg:grid-cols-[5fr_7fr] bg-background">
    <aside
      class="relative hidden md:flex flex-col justify-between p-6 md:p-8 lg:p-12 xl:p-16 text-white overflow-hidden bg-gradient-to-br from-[var(--dodger-blue)] via-[var(--azure-blue)] to-[var(--cobalt-blue)]"
    >
      <!-- Overlay oscuro para garantizar contraste del texto blanco sobre las zonas claras del degradado -->
      <div class="absolute inset-0 bg-gradient-to-br from-black/55 via-black/30 to-black/45" />

      <div class="absolute inset-0 opacity-10 [background-image:radial-gradient(circle_at_20%_20%,white_1px,transparent_1px)] [background-size:32px_32px]" />
      <div class="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
      <div class="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-[var(--neon-pink)]/10 blur-3xl" />

      <div class="relative z-10 flex items-center gap-3">
        <img src="@/assets/combined_logo.png" alt="CTI CRM" class="h-10 md:h-11 lg:h-12 w-auto" />
      </div>

      <div class="relative z-10 space-y-4 md:space-y-6">
        <h1 class="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight tracking-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)]">
          Centro de Contacto<br />Inteligente
        </h1>
        <p class="text-base md:text-lg text-white/90 max-w-sm md:max-w-md drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
          Define una nueva contraseña segura para proteger tu cuenta.
        </p>
        <ul class="space-y-2 md:space-y-3 text-white/95">
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <Zap class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Dashboard en tiempo real</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <BarChart3 class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Analítica avanzada de conversión</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <ShieldCheck class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Roles y permisos por equipo</span>
          </li>
        </ul>
      </div>

      <p class="relative z-10 text-xs md:text-sm text-white/60">Data Lean Makers, S.L. © {{ new Date().getFullYear() }}</p>
    </aside>

    <main class="flex items-center justify-center p-4 sm:p-6 md:p-10 lg:p-12">
      <div class="w-full max-w-sm space-y-6 md:max-w-md">
        <div class="md:hidden flex justify-center">
          <img src="@/assets/combined_logo.png" alt="CTI CRM" class="h-12 w-auto" />
        </div>

        <header v-if="!reset" class="space-y-1.5">
          <h2 class="text-xl sm:text-2xl font-semibold tracking-tight">Nueva contraseña</h2>
          <p class="text-sm text-muted-foreground">Ingresa tu nueva contraseña para restablecer el acceso.</p>
        </header>

        <form v-if="!reset" class="space-y-4" @submit.prevent="handleSubmit">
          <div class="space-y-2">
            <Label for="password">Nueva contraseña</Label>
            <div class="relative">
              <Lock class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                v-model="form.password"
                type="password"
                autocomplete="new-password"
                name="password"
                placeholder="Mínimo 8 caracteres"
                class="pl-9"
                :aria-invalid="!!passwordError"
                :aria-describedby="passwordError ? 'password-error' : undefined"
                @blur="touched.password = true"
              />
            </div>
            <p v-if="passwordError" id="password-error" class="text-xs text-destructive">{{ passwordError }}</p>
          </div>

          <div class="space-y-2">
            <Label for="confirmPassword">Confirmar contraseña</Label>
            <div class="relative">
              <Lock class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="confirmPassword"
                v-model="form.confirmPassword"
                type="password"
                autocomplete="new-password"
                name="confirmPassword"
                placeholder="Repite la contraseña"
                class="pl-9"
                :aria-invalid="!!confirmPasswordError"
                :aria-describedby="confirmPasswordError ? 'confirm-error' : undefined"
                @blur="touched.confirmPassword = true"
              />
            </div>
            <p v-if="confirmPasswordError" id="confirm-error" class="text-xs text-destructive">{{ confirmPasswordError }}</p>
          </div>

          <Button type="submit" class="w-full gap-2" :disabled="loading">
            <Loader2 v-if="loading" class="h-4 w-4 animate-spin" />
            <ArrowRight v-else class="h-4 w-4" />
            <span>Restablecer contraseña</span>
          </Button>
        </form>

        <div v-else class="space-y-6 text-center">
          <div class="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10">
            <CheckCircle2 class="h-6 w-6 text-primary" />
          </div>
          <div class="space-y-1.5">
            <h2 class="text-2xl font-semibold tracking-tight">Contraseña actualizada</h2>
            <p class="text-sm text-muted-foreground">
              Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión.
            </p>
          </div>
          <Button class="w-full gap-2" @click="router.push('/login')">
            <ArrowLeft class="h-4 w-4" />
            <span>Iniciar sesión</span>
          </Button>
        </div>
      </div>
    </main>
  </div>
</template>